import {
  Dispatch, SetStateAction, useEffect, useRef, useState,
} from 'react'

import { APP_SHELL_COLLAPSED_MEDIA_QUERY } from '../constants/responsive'
import { useMediaQuery } from './useMediaQuery'

export interface AppShellSidebar {
  /** Sidebar đang mở — ≥1024px là cột cố định, ≤1023px là lớp phủ mở bằng nút hamburger. */
  isShowSideBar: boolean
  setIsShowSideBar: Dispatch<SetStateAction<boolean>>
  /** Khung nhìn ≤1023px (`APP_SHELL_COLLAPSED_MEDIA_QUERY`) — nội dung bỏ lề trái 300px. */
  shellCollapsed: boolean
}

/**
 * Trạng thái sidebar của app shell (`views/defaultLayout`), theo dõi mốc 1023px khi đổi bề rộng.
 *
 * 🔴 Lỗi đo được ở Task 7 (E2E `topup-responsive.mjs`, 14/09/2026): shell đọc `window.innerWidth` MỘT LẦN lúc mount
 * (`useState(window.innerWidth > 1023)`, lề trái `isMobileDevice()` chỉ tính lại khi layout tình cờ render lại) ⇒ xoay
 * iPad ngang (1024) → dọc (768) vẫn ghim sidebar 300px, nội dung còn ~468px; kéo cửa sổ 1440 → 375 thì trang tràn ngang
 * (scrollWidth 647 / innerWidth 375).
 *
 * - Lượt đầu giống hệt bản cũ: `isShowSideBar = !shellCollapsed` (không có `matchMedia` ⇒ rộng ⇒ hiện sidebar).
 * - CHỈ khi bề rộng VƯỢT mốc (so với giá trị trước, nhớ trong `useRef`): sang thu gọn ⇒ đóng sidebar, sang rộng ⇒ mở lại.
 * - 🔴 Khoá cuộn `body`: CHỈ gỡ khi vượt thu gọn → rộng lúc lớp phủ ĐANG MỞ (khoá đó do nút hamburger đặt, sang desktop
 *   không gỡ thì trang không cuộn được), và gỡ bằng `removeProperty` đúng hai thuộc tính inline hamburger đặt. KHÔNG
 *   dùng `enableScroll()`: nó ghi `overflow: unset` inline, đè khoá stylesheet `html body { overflow-y: hidden }` của
 *   antd 5.24 (Modal/Drawer) suốt phiên và gỡ luôn `disableScroll()` của 4 trang danh sách SIM đang mở drawer lọc.
 *   Rộng → thu gọn không đụng `body`.
 * - Không vượt mốc ⇒ KHÔNG đụng gì: người dùng tự mở/đóng lớp phủ trên điện thoại không bị ghi đè (bàn phím ảo đổi
 *   chiều cao không đổi query bề rộng). Phép so với `previousCollapsed` chặn cả lượt MOUNT và lượt render lại khi
 *   `isShowSideBar` đổi (có trong deps chỉ để đọc đúng giá trị lúc vượt mốc).
 *
 * `Header`/`SideBar` vẫn tự gọi `isMobileDevice()` lúc render; chúng render lại theo `DefaultLayout` khi query đổi.
 */
export const useAppShellSidebar = (): AppShellSidebar => {
  const shellCollapsed = useMediaQuery(APP_SHELL_COLLAPSED_MEDIA_QUERY)
  const [isShowSideBar, setIsShowSideBar] = useState(() => !shellCollapsed)
  const previousCollapsed = useRef(shellCollapsed)

  useEffect(() => {
    if (previousCollapsed.current === shellCollapsed) return
    previousCollapsed.current = shellCollapsed
    // `isShowSideBar` ở đây là giá trị của lượt render VƯỢT mốc — ở khung thu gọn nghĩa là lớp phủ đang mở.
    if (!shellCollapsed && isShowSideBar) {
      document.body.style.removeProperty('overflow')
      document.body.style.removeProperty('height')
    }
    setIsShowSideBar(!shellCollapsed)
  }, [shellCollapsed, isShowSideBar])

  return { isShowSideBar, setIsShowSideBar, shellCollapsed }
}
